'use client';

import { TimePickerInput, TimePickerProvider, type TimeType } from '@repo/features/time-picker';
import { withFieldGroup } from '@repo/form';
import { Field, FieldLabel, Tabs, TabsList, TabsTrigger } from '@repo/ui/components';
import { cn } from '@repo/ui/lib/utils';
import { type ReactNode, useId } from 'react';
import { getStopTimeValue, getTimeLabel, type StopFieldValues } from '@/app/@protected/_components/load-quick-create/quick-create-stop-field.tsx';
const TIME_TYPES = ['between', 'at', 'by', 'tbc', 'asap'] as const satisfies TimeType[];
// A window copied as `24` keeps its own tab, so the copy is never invalid.
const ALL_DAY_TIME_TYPES = [...TIME_TYPES, '24'] as const satisfies TimeType[];
const DEFAULT_VALUES: StopFieldValues = {
  date: undefined,
  id: 'duplicate-stop-default',
  place: null,
  reference_no: null,
  time: {
    type: 'tbc'
  },
  type: 'collection'
};
type DuplicateStopFieldsProps = {
  /** The Stop was copied with a 24-hour window. */
  allDay: boolean;
  /** What follows the date and time, such as the reference. */
  children?: ReactNode;
  /**
   * `grid`: a row of three columns, as the dialog shows it; `column`: one field under another, the
   * time tabs without their label, as the side panel shows it.
   */
  layout: 'column' | 'grid';
  /** The earliest date the calendar offers. */
  minDate?: Date;
  pending: boolean;
  stopNumber: number;
};
const DUPLICATE_STOP_FIELDS_PROPS: DuplicateStopFieldsProps = {
  allDay: false,
  children: undefined,
  layout: 'grid',
  minDate: undefined,
  pending: false,
  stopNumber: 1
};

/**
 * One Stop of a Duplicate: its date, labelled by its type, and its time window as tabs over the
 * times, then `children`. Its location never changes, so it isn't shown.
 */
export const DuplicateStopFields = withFieldGroup({
  defaultValues: DEFAULT_VALUES,
  props: DUPLICATE_STOP_FIELDS_PROPS,
  render: function Render({
    allDay,
    children,
    group,
    layout,
    minDate,
    pending,
    stopNumber
  }) {
    const timeLabelId = useId();
    const grid = layout === 'grid';
    const stopType = group.state.values.type === 'collection' ? 'Collection' : 'Delivery';
    const timeTypes = allDay ? ALL_DAY_TIME_TYPES : TIME_TYPES;
    return (
      // A group rather than a fieldset, which can't take the card's columns as a subgrid; every
      // control disables itself while pending.
      <div aria-label={`Stop ${stopNumber}`}
      // A row of the card's three columns once it has room for them, as the full-width dialog
      // has; otherwise one column.
      className={grid ? 'grid grid-cols-[minmax(0,1fr)] items-start gap-6 @min-[48rem]/duplicate-card:col-span-3 @min-[48rem]/duplicate-card:grid-cols-subgrid' : 'flex flex-col gap-3'} role="group">
        <group.AppField name="date">
          {field => <field.DatePicker autoFocus calendarDisable={minDate ? {
            before: minDate
          } : undefined} contentClassName={grid ? 'gap-3' : 'gap-1.5'} disabled={pending} label={`${stopType} date`} triggerClassName="w-full justify-start" />}
        </group.AppField>
        <group.Field name="time">
          {field =>
          // In the column, the tabs sit closer to their times than to the next field, so the two
          // read as one field.
          <Field className={grid ? 'gap-3' : 'gap-1'}>
              <FieldLabel className={cn(!grid && 'sr-only')} id={timeLabelId}>
                {stopType} time
              </FieldLabel>
              <Tabs onValueChange={value => {
              const type = timeTypes.find(item => item === value);
              if (type) field.handleChange(getStopTimeValue(type));
            }} value={field.state.value.type}>
                {/* Too narrow for every tab, as on a phone, they scroll. */}
                <TabsList aria-labelledby={timeLabelId} className="max-w-full overflow-x-auto @min-[48rem]/duplicate-card:max-w-none @min-[48rem]/duplicate-card:overflow-visible">
                  {timeTypes.map(type => <TabsTrigger disabled={pending} key={type} value={type}>
                      {getTimeLabel(type)}
                    </TabsTrigger>)}
                </TabsList>
              </Tabs>
              <TimePickerProvider disabled={pending} mode="schema" onChange={field.handleChange} value={field.state.value}>
                <div className="flex items-center gap-1 empty:hidden" onBlurCapture={field.handleBlur}>
                  <TimePickerInput />
                </div>
              </TimePickerProvider>
            </Field>}
        </group.Field>
        {children}
      </div>
    );
  }
});